import React, { useState, useRef, useEffect } from 'react';
import { useToast } from '../services/toastContext';

/**
 * Renders the global toast stack in a fixed top-right container.
 */
const ToastContainer = () => {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
};

/**
 * Individual toast with enter/exit animation.
 */
const ToastItem = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    if (isExiting) return;
    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => onDismiss(toast.id), 200);
  };

  const handleActionClick = () => {
    if (toast.action?.onClick) {
      toast.action.onClick();
    }
    handleDismiss();
  };

  const typeClass = toast.type ? `toast--${toast.type}` : 'toast--info';

  return (
    <div
      className={`toast ${typeClass} ${isExiting ? 'toast--exiting' : ''}`}
      role={toast.type === 'error' ? 'alert' : undefined}
    >
      <div className="toast__body">
        <div className="toast__content">
          <span className="toast__message">{toast.message}</span>
          {toast.description && (
            <span className="toast__description">{toast.description}</span>
          )}
        </div>
        <button
          type="button"
          className="toast__close"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {toast.action && (
        <button
          type="button"
          className="toast__action"
          onClick={handleActionClick}
        >
          {toast.action.label}
        </button>
      )}
    </div>
  );
};

export default ToastContainer;
